<template>
  <DialogTemplate
    v-bind="props"
    ref="element"
    align="center"
    class="dialog"
    :show-controls="true"
    theme="secondary-dark"
    @close="emit('close')"
  >
    <DialogTrophiesEffect :delay="500" />
    <DialogTrophiesInfos
      :icon-positions="iconPositions"
      :trophies="trophies"
      :index="selectedIndex"
      :offset-x="offsetX"
      class="infos"
    />

    <div class="contents">
      <div ref="borderElement" class="border" />
      <DialogTrophiesIcons
        ref="iconsElement"
        :trophies="trophies"
        :medias="medias"
        class="dialog-trophies-icons"
        @scroll="handleScrollIcons"
        @complete="handlCompleteShowingIcons"
        @select="handleSelect"
        @update-position="handleUpdatePosition"
      />
      <div class="texts">
        <h2 ref="titleElement" class="title">{{ title }}</h2>
        <p ref="detailsElement" class="details">{{ details }}</p>
      </div>
    </div>

    <template #footer>
      <ButtonBase
        ref="buttonElement"
        rounded
        class="button"
        theme="secondary"
        variant="tonal"
        stroke="n"
        to="/gaming#trophees"
        @click="() => dialog.close()"
      >
        Voir tous mes trophées
      </ButtonBase>
    </template>
  </DialogTemplate>
</template>
<script lang="ts" setup>
import { gsap } from 'gsap';

interface Props {
  trophies: TrophySchema[];
  medias: Map<string, MediaSchema>;
}

const selectedIndex = ref<number>(-1);

const offsetX = ref<number>(0);

const props = defineProps({
  trophies: null,
  medias: null
});

const element = ref();

const { x } = useElementBounding(element);

const itemPositions = ref<number[]>([...Array(props.trophies.length).keys()]);

const handleUpdatePosition = (index: number, position: number) => {
  itemPositions.value[index] = position;
};

const iconPositions = ref<number[]>([...Array(props.trophies.length).keys()]);

watchEffect(() => {
  iconPositions.value = itemPositions.value.map((pos) => pos - x.value);
});

const count = computed(() => props.trophies.length);

const title = computed(() => {
  if (count.value === 1) {
    return `Bravo ! Tu viens de débloquer 1 trophée ! 🏆`;
  }
  return `Bravo ! Tu viens de débloquer ${count.value} trophées ! 🏆`;
});

const details = computed(() => {
  return 'Chaque jour compte : continue à réviser pour débloquer encore plus de trophées !';
});

const borderElement = ref(null);

const iconsElement = ref(null);

const detailsElement = ref(null);

const titleElement = ref(null);

const buttonElement = ref(null);

const emit = defineEmits(["close"]);

const dialog = useDialogStore();

const handleSelect = (index: number) => {
  selectedIndex.value = index;
};

const handlCompleteShowingIcons = () => {
  // console.info('timeline.value', timeline.value);
};

const handleScrollIcons = (scrollX: number) => {
  offsetX.value = -scrollX;
};

const _completeIntro = () => {
  // gsap.to(iconsElement.value.$el, { scrollLeft: 1 });
  const scrollEvent = new Event('scroll', {
    bubbles: true, // Allows the event to bubble up through the DOM
    cancelable: true, // Indicates whether the event can be canceled
  });

  // Dispatch the scroll event to force resize
  const element = document.querySelector('.dialog-trophies-icons');
  element?.dispatchEvent(scrollEvent);
  // select item info
  selectedIndex.value = 0;
};

onMounted(() => {
  const timeline = {
    value: null,
  };
  timeline.value = gsap.timeline({ delay: 0.4, onComplete: _completeIntro });

  timeline.value.fromTo(
    borderElement.value,
    { alpha: 0 },
    { delay: 0, alpha: 1, duration: 0.2, ease: 'power4.out' },
    0.8
  );
  timeline.value.fromTo(
    borderElement.value,
    { scale: 1.1 },
    { scale: 1, alpha: 1, duration: 0.5, ease: 'power4.out' },
    0.8
  );

  timeline.value.fromTo(
    titleElement.value,
    { alpha: 0, scale: 0.5 },
    { delay: 0, alpha: 1, scale: 1, duration: 1, ease: 'elastic.out(1,0.4)' },
    0.4
  );
  timeline.value.fromTo(
    detailsElement.value,
    { alpha: 0, scale: 0.95, y: 2 },
    {
      delay: 0,
      alpha: 1,
      scale: 1,
      y: 0,
      duration: 0.7,
      ease: 'quad.out',
    },
    0.6
  );
  timeline.value.fromTo(
    buttonElement.value.$el,
    { alpha: 0, scale: 1.1, y: 5 },
    {
      scale: 1,
      alpha: 1,
      y: 0,
      duration: 0.3,
      ease: 'quad.out',
    },
    1
  );
  // timeline.value.pause();
  // timeline.value.resume();
});
</script>
<style lang="scss" scoped>
.dialog {
  position: relative;
  width: calc(3 * 9rem + 6 * var(--gap_l) + 2 * var(--stroke_thickness_m));
  max-width: auto;

  // padding: var(--gap_l);
  padding: var(--gap_l) 0;
  overflow: hidden;
  background-color: var(--color_secondary_darker);
  text-align: center;
  gap: var(--gap_l);
}

.contents {
  display: flex;
  position: relative;
  flex-direction: column;
  align-items: center;
  width: stretch;
}

.border {
  position: absolute;
  z-index: 0;
  top: 4.5rem;
  left: var(--gap_l);
  width: calc(100% - 2 * var(--gap_l));
  height: calc(100% - 4rem);
  border-color: rgb(231 227 255 / 20%);
  @include stroke('n', 'm');
}

.texts {
  display: flex;
  z-index: 1;
  flex-direction: column;
  align-items: center;
  padding: 0 var(--gap_xxl) var(--gap_l);
  gap: var(--gap_l);
}

.title {
  @include font('xl', 700, 'secondary');

  color: var(--color_neutral_25);
  text-align: center;
}

.details {
  /* Chaque jour compte : continue à réviser pour débloquer encore plus de trophées ! */
  @include font('n', 400, 'secondary');

  color: var(--color_neutral_25);
}

:deep(footer) {
  display: flex;
  align-items: center;
  justify-content: center;
}

.tooltip-zone {
  height: 12rem;
}

.tooltip {
  position: absolute;
  z-index: 3;
  top: -12rem;
  left: 10%;
  width: 10rem;
  height: 10rem;
  background-color: red;
}
</style>
